import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Container,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { Link } from "react-router-dom";
import classnames from "classnames";
import api from "../../api";
import InboxSidebar from "./Sidebar"; // or "./Sidebar" if you renamed it
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { messageState } from "../../Recoil/atom";
import { useRecoilState } from "recoil";
import { useNavigate } from "react-router-dom";

const Inbox = () => {
  document.title = "Inbox  | Pokémon Radiance";

  // "1" = Primary (Received), "3" = Sent (mirroring your NavLink logic)
  const [customActiveTab, setCustomActiveTab] = useState("1");
  const [messages, setMessages] = useRecoilState(messageState);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  // For selecting messages
  const [selectedIds, setSelectedIds] = useState([]);

  // For delete-all confirmation
  const [deleteAllModal, setDeleteAllModal] = useState(false);

  // Switch between "Primary" and "Sent"
  const toggleCustom = (tab) => {
    if (customActiveTab !== tab) {
      setCustomActiveTab(tab);
    }
  };

  // ---------------------------
  // Fetch messages from backend
  // ---------------------------
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        let endpoint =
          customActiveTab === "1"
            ? "/v1/messages/received"
            : customActiveTab === "3"
              ? "/v1/messages/sent"
              : "";

        if (!endpoint) return;
        const response = await api.get(`${endpoint}`, {
          withCredentials: true,
        });

        const messagesData =
          response.data.received_messages || response.data.sent_messages || [];
        setMessages(messagesData);

        // Clear selection if user switches tabs
        setSelectedIds([]);
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    };

    fetchMessages();
  }, [customActiveTab, setMessages]);

  // ---------------------------------
  // Handle checkboxes for selected IDs
  // ---------------------------------
  const handleCheckboxChange = (messageId) => {
    setSelectedIds((prev) =>
      prev.includes(messageId)
        ? prev.filter((id) => id !== messageId)
        : [...prev, messageId],
    );
  };

  // -----------------------------------------------------
  // Delete selected messages (calls parent's handleDelete)
  // -----------------------------------------------------
  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return; // no selection
    try {
      // Perform multiple DELETE requests in parallel
      const deleteRequests = selectedIds.map((id) =>
        api.delete(`/v1/messages/delete/${id}`, {
          withCredentials: true,
        }),
      );
      await Promise.all(deleteRequests);

      // Remove from local state
      setMessages((prev) =>
        prev.filter((msg) => !selectedIds.includes(msg.id)),
      );
      setSelectedIds([]);
    } catch (err) {
      console.error("Error deleting selected messages:", err);
    }
  };

  // -------------
  // Delete All UI
  // -------------
  const openDeleteAllModal = () => setDeleteAllModal(true);
  const closeDeleteAllModal = () => setDeleteAllModal(false);

  // -------------
  // Delete All
  // -------------
  const handleDeleteAll = async () => {
    try {
      // Calls your "delete all received" endpoint
      await api.delete("/v1/messages/delete/received", {
        withCredentials: true,
      });

      // Clear out the messages in local state
      setMessages([]);
    } catch (err) {
      console.error("Error deleting all received messages:", err);
    } finally {
      closeDeleteAllModal();
    }
  };

  const handleReportMessage = async (messageId) => {
    try {
      await api.post(
        `/v1/messages/report/${messageId}`,
        {},
        {
          withCredentials: true, // or whatever your config is
        },
      );

      // Update local messages array
      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? { ...msg, report: 1 } : msg)),
      );

      // Also update the selected message itself (so the modal sees the change)
      setSelectedMessage((prev) => (prev ? { ...prev, report: 1 } : null));

      // Let user know, including staff message
      alert(
        `Message has been reported! A staff member will view the message soon.`,
      );

      // Close the modal
      setModalOpen(false);
    } catch (err) {
      console.error("Error reporting message:", err);
      alert("Failed to report message.");
    }
  };

  // ------------------------
  // Viewing a message => mark as viewed
  // ------------------------
  const handleViewMessage = async (message) => {
    try {
      // Mark the message as viewed in the backend
      await api.patch(`/v1/messages/${message.id}/view`, null, {
        withCredentials: true,
      });

      // Update local state so that it’s no longer “unviewed”
      setMessages((prev) =>
        prev.map((m) => (m.id === message.id ? { ...m, view: 1 } : m)),
      );
    } catch (err) {
      console.error("Error marking message as viewed:", err);
    }

    console.log(message);

    // Open the modal
    setSelectedMessage(message);
    setModalOpen(true);
  };

  // -------------------
  // Close the modal
  // -------------------
  const toggleModal = () => setModalOpen(!modalOpen);

  // ----------------------
  // Reply button
  // ----------------------
  const handleReply = (username) => {
    navigate(`/send-message?to=${username}`);
  };

  const dateStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "150px", // Adjust based on your layout
  };

  const renderMessageRow = (message, index, isSent) => {
    const fromUser = message.userfrom || message.from;
    const toUser = message.userto || message.to;
    const counterparty = isSent ? toUser : fromUser;
    const isUnviewed = !isSent && message.view === 0;

    return (
      <div
        key={message.id}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "12px 12px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(0,0,0,0.15)",
          borderRadius: 10,
          marginBottom: 10,
          cursor: "pointer",
        }}
        onClick={() => handleViewMessage(message)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          {!isSent && (
            <input
              type="checkbox"
              checked={selectedIds.includes(message.id)}
              onClick={(e) => e.stopPropagation()}
              onChange={() => handleCheckboxChange(message.id)}
            />
          )}

          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 700,
                  color: "#fff",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {message.title ? message.title : "(No subject)"}
              </div>
              {isSent ? (
                <span className="bg-primary badge">Sent</span>
              ) : isUnviewed ? (
                <span className="bg-warning badge">Unviewed</span>
              ) : (
                <span className="bg-secondary badge">Viewed</span>
              )}
            </div>
            <div
              style={{
                color: "rgba(255,255,255,0.75)",
                fontSize: 13,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              {String(message.content || "").slice(0, 70)}
              {String(message.content || "").length > 70 ? "..." : ""}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 10,
            flexShrink: 0,
          }}
        >
          {counterparty ? (
            <Link
              to={`/playerprofile?user=${counterparty}`}
              onClick={(e) => e.stopPropagation()}
              style={{ textDecoration: "none", color: "#cfe3ff", fontWeight: 700 }}
            >
              {counterparty}
            </Link>
          ) : (
            <span style={{ color: "rgba(255,255,255,0.6)" }}>Unknown</span>
          )}
          {message.date && (
            <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, ...dateStyle }}>
              {message.date}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Messages" breadcrumbItem="Inbox" />
          <Row>
            {/* SIDEBAR, with delete controls */}
            <InboxSidebar
              isPrimaryTabActive={customActiveTab === "1"}
              selectedIds={selectedIds}
              handleDeleteSelected={handleDeleteSelected}
              openDeleteAllModal={openDeleteAllModal}
            />

            {/* MAIN CONTENT */}
            <Col xl={9}>
              <Card className="mb-0" style={{ background: "rgba(0,0,0,0.15)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <CardBody>
                  {/* Nav for Primary (1) vs Sent (3) */}
                  <Nav tabs className="nav-tabs-custom nav-justified">
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: customActiveTab === "1",
                        })}
                        onClick={() => toggleCustom("1")}
                      >
                        <i className="mdi mdi-inbox me-2 align-middle font-size-18"></i>
                        <span className="d-none d-md-inline-block">Inbox</span>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: customActiveTab === "3",
                        })}
                        onClick={() => toggleCustom("3")}
                      >
                        <i className="mdi mdi-email-newsletter me-2 align-middle font-size-18"></i>
                        <span className="d-none d-md-inline-block">Sent</span>
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent
                    activeTab={customActiveTab}
                    className="p-3 text-muted"
                  >
                    {/* Primary Tab */}
                    <TabPane tabId="1">
                      <Row>
                        <Col sm="12">
                          {messages.length > 0 ? (
                            <div>
                              {messages.map((message, index) =>
                                renderMessageRow(message, index, false),
                              )}
                            </div>
                          ) : (
                            <div style={{
                              padding: "30px 12px",
                              textAlign: "center",
                              color: "rgba(255,255,255,0.75)",
                            }}>
                              No Messages
                            </div>
                          )}
                        </Col>
                      </Row>
                    </TabPane>

                    {/* Sent Tab */}
                    <TabPane tabId="3">
                      <React.Fragment>
                        {/* Your message rendering code */}
                        <Row>
                          <Col sm="12">
                            {messages.length > 0 ? (
                              <div>
                                {messages.map((message, index) =>
                                  renderMessageRow(message, index, true),
                                )}
                              </div>
                            ) : (
                              <div style={{
                                padding: "30px 12px",
                                textAlign: "center",
                                color: "rgba(255,255,255,0.75)",
                              }}>
                                No Messages
                              </div>
                            )}
                          </Col>
                        </Row>
                      </React.Fragment>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal for viewing a single message (Bags-of-Gems style) */}
        {modalOpen && selectedMessage && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.75)",
              backdropFilter: "blur(5px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
              padding: 12,
            }}
            onClick={toggleModal}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: "rgba(25, 25, 35, 0.95)",
                borderRadius: 12,
                width: "95%",
                maxWidth: 720,
                boxShadow:
                  "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                overflow: "hidden",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "rgba(0, 0, 0, 0.2)",
                  gap: 10,
                }}
              >
                <h5 style={{ margin: 0, color: "#fff", fontWeight: 600, minWidth: 0 }}>
                  {selectedMessage?.title ?? "Message"}
                </h5>
                <button
                  onClick={toggleModal}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#fff",
                    fontSize: 20,
                    cursor: "pointer",
                    padding: "8px 12px",
                    borderRadius: 4,
                    opacity: 0.7,
                    flexShrink: 0,
                    minWidth: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => (e.target.style.opacity = "1")}
                  onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
                >
                  ✕
                </button>
              </div>

              <div
                style={{
                  padding: "16px 20px",
                  color: "#fff",
                  maxHeight: "60vh",
                  overflowY: "auto",
                }}
              >
                <div style={{ color: "rgba(255,255,255,0.8)", marginBottom: 10 }}>
                  <strong>{customActiveTab === "3" ? "To:" : "From:"}</strong>{" "}
                  <span style={{ color: "#cfe3ff", fontWeight: 700 }}>
                    {customActiveTab === "3"
                      ? selectedMessage.to || selectedMessage.userto
                      : selectedMessage.from || selectedMessage.userfrom}
                  </span>
                </div>

                <div
                  style={{
                    background: "rgba(40, 44, 52, 0.45)",
                    borderRadius: 10,
                    padding: 14,
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.5,
                    color: "rgba(255,255,255,0.92)",
                  }}
                >
                  {selectedMessage.content}
                </div>
              </div>

              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  background: "rgba(0, 0, 0, 0.2)",
                  flexWrap: "wrap",
                }}
              >
                {customActiveTab === "1" &&
                  (selectedMessage.report === 1 ? (
                    <button
                      disabled
                      style={{
                        background: "rgba(220, 53, 69, 0.35)",
                        border: "1px solid rgba(220, 53, 69, 0.55)",
                        color: "#fff",
                        padding: "8px 14px",
                        borderRadius: 6,
                        cursor: "not-allowed",
                        opacity: 0.8,
                      }}
                    >
                      Reported
                    </button>
                  ) : (
                    <button
                      onClick={() => handleReportMessage(selectedMessage.id)}
                      style={{
                        background: "rgba(220, 53, 69, 0.7)",
                        border: "1px solid rgba(220, 53, 69, 0.9)",
                        color: "#fff",
                        padding: "8px 14px",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      Report
                    </button>
                  ))}

                <button
                  onClick={() =>
                    handleReply(
                      customActiveTab === "3"
                        ? selectedMessage.to || selectedMessage.userto
                        : selectedMessage.from || selectedMessage.userfrom,
                    )
                  }
                  style={{
                    background: "rgba(74, 144, 226, 0.8)",
                    border: "1px solid rgba(74, 144, 226, 0.95)",
                    color: "#fff",
                    padding: "8px 14px",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                >
                  Reply
                </button>

                <button
                  onClick={toggleModal}
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    color: "#fff",
                    padding: "8px 14px",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete All Confirmation Modal (Bags-of-Gems style) */}
        {deleteAllModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.75)",
              backdropFilter: "blur(5px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
              padding: 12,
            }}
            onClick={closeDeleteAllModal}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: "rgba(25, 25, 35, 0.95)",
                borderRadius: 12,
                width: "95%",
                maxWidth: 520,
                boxShadow:
                  "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                overflow: "hidden",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "rgba(0, 0, 0, 0.2)",
                  gap: 10,
                }}
              >
                <h5 style={{ margin: 0, color: "#fff", fontWeight: 600 }}>
                  Confirm Delete All Messages
                </h5>
                <button
                  onClick={closeDeleteAllModal}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#fff",
                    fontSize: 20,
                    cursor: "pointer",
                    padding: "8px 12px",
                    borderRadius: 4,
                    opacity: 0.7,
                    flexShrink: 0,
                    minWidth: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => (e.target.style.opacity = "1")}
                  onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
                >
                  ✕
                </button>
              </div>

              <div style={{ padding: "18px 20px", color: "#fff" }}>
                <div
                  style={{
                    background: "rgba(220, 53, 69, 0.12)",
                    border: "1px solid rgba(220, 53, 69, 0.35)",
                    borderRadius: 10,
                    padding: 14,
                    color: "rgba(255,255,255,0.92)",
                    lineHeight: 1.4,
                  }}
                >
                  Are you sure you want to delete all messages? This action cannot be undone.
                </div>
              </div>

              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  background: "rgba(0, 0, 0, 0.2)",
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={closeDeleteAllModal}
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    color: "#fff",
                    padding: "8px 14px",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAll}
                  style={{
                    background: "rgba(220, 53, 69, 0.8)",
                    border: "1px solid rgba(220, 53, 69, 0.95)",
                    color: "#fff",
                    padding: "8px 14px",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: 800,
                  }}
                >
                  Delete All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Inbox;
